import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const CUSTOM_DROPDOWN_CLOSE_DELAY = 320;
const CUSTOM_DROPDOWN_CLOSE_TRANSITION_FALLBACK = 320;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  #activationGeneration = 0;

  /** @type {Element | null} */
  #hydrationSection = null;

  /** @type {HTMLElement | null} */
  #hydrationTrigger = null;

  /** @type {HTMLElement | null} */
  #hydrationFocus = null;

  /**
   * @type {Map<string, HTMLElement>}
   */
  #customDropdownByTrigger = new Map();

  /**
   * @type {HTMLElement | null}
   */
  #activeCustomDropdown = null;

  /** @type {HTMLElement | null} */
  #formatsFallback = null;

  /** @type {HTMLElement | null} */
  #navigationContainer = null;

  /**
   * @type {number | null}
   */
  #customDropdownCloseTimeout = null;

  /**
   * @type {Map<HTMLElement, () => void>}
   */
  #customDropdownCloseCleanups = new Map();

  /**
   * @type {{ x: number; y: number } | null}
   */
  #lastPointerPosition = null;

  /**
   * @type {{ x: number; y: number } | null}
   */
  #previousPointerPosition = null;

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', this.#handleOverflowMenuPointerLeave);
    window.addEventListener('pointermove', this.#handleWindowPointerMove, { passive: true });
    this.addEventListener('legacybox:header-formats-changed', this.#handleFormatsChanged);
    this.#hydrationSection = this.closest('.shopify-section');
    this.#hydrationSection?.addEventListener('theme:section-hydrating', this.#handleHydrating);
    this.#hydrationSection?.addEventListener('theme:section-hydrated', this.#handleHydrated);
    this.#setupCustomDropdowns();
    this.#syncFormatsFallback();
    // on load, cache the max height of the submenu so you can use it in a translate
    this.#cacheMaxOverflowMenuHeight();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    window.removeEventListener('pointermove', this.#handleWindowPointerMove);
    this.removeEventListener('legacybox:header-formats-changed', this.#handleFormatsChanged);
    this.#hydrationSection?.removeEventListener('theme:section-hydrating', this.#handleHydrating);
    this.#hydrationSection?.removeEventListener('theme:section-hydrated', this.#handleHydrated);
    this.#hydrationSection = null;
    this.overflowMenu?.removeEventListener('pointerleave', this.#handleOverflowMenuPointerLeave);
    this.#unbindFormatsFallback();
    this.#unbindNavigationContainer();
    this.#teardownCustomDropdowns();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    this.#resetMenuState({ resetPointer: true });
    this.#cacheMaxOverflowMenuHeight();
    setHeaderMenuStyle();
  }, 100);

  #handleFormatsChanged = () => {
    this.#teardownCustomDropdowns();
    this.#setupCustomDropdowns();
    this.#syncFormatsFallback();
    this.#cacheMaxOverflowMenuHeight();
  };

  #handleHydrating = () => {
    const activeItem = this.#state.activeItem;
    const candidates = [...this.querySelectorAll('[ref="menuitem"]'), this.#formatsFallback];
    this.#hydrationTrigger = /** @type {HTMLElement | null} */ (candidates.find((item) =>
      item instanceof HTMLElement && item.matches(':hover, :focus-within')
    ) ?? (this.#activeCustomDropdown?.matches(':hover, :focus-within') ? activeItem : null));
    this.#hydrationFocus = document.activeElement instanceof HTMLElement &&
      this.#activeCustomDropdown?.contains(document.activeElement) ? document.activeElement : null;
  };

  #handleHydrated = () => {
    const trigger = this.#hydrationTrigger;
    const focused = this.#hydrationFocus;
    this.#hydrationTrigger = null;
    this.#hydrationFocus = null;
    this.#teardownCustomDropdowns();
    this.#setupCustomDropdowns();
    this.#syncFormatsFallback();
    this.#cacheMaxOverflowMenuHeight();
    if (trigger?.isConnected && this.headerComponent?.dataset.menuStyle === 'menu') {
      this.#activateMenuItem(trigger);
      if (focused?.isConnected) focused.focus({ preventScroll: true });
    }
  };

  #handleFormatsFallbackDeactivate = (event) => this.deactivate(event);

  #unbindFormatsFallback() {
    const item = this.#formatsFallback;
    if (!item) return;
    item.removeEventListener('pointerenter', this.activate);
    item.removeEventListener('focusin', this.activate);
    item.removeEventListener('pointerleave', this.#handleFormatsFallbackDeactivate);
    item.removeEventListener('blur', this.#handleFormatsFallbackDeactivate);
    item.removeEventListener('keydown', this.#handleCustomTriggerKeyDown);
    item.removeAttribute('aria-haspopup');
    item.removeAttribute('aria-controls');
    item.removeAttribute('aria-expanded');
    this.#formatsFallback = null;
  }

  #unbindNavigationContainer() {
    this.#navigationContainer?.removeEventListener('pointerover', this.#handleNavigationEnter);
    this.#navigationContainer?.removeEventListener('focusin', this.#handleNavigationEnter);
    this.#navigationContainer = null;
  }

  #syncNavigationContainer() {
    const container = this.parentElement;
    if (container === this.#navigationContainer) return;
    this.#unbindNavigationContainer();
    if (!container) return;
    this.#navigationContainer = container;
    container.addEventListener('pointerover', this.#handleNavigationEnter);
    container.addEventListener('focusin', this.#handleNavigationEnter);
  }

  #handleNavigationEnter = (event) => {
    if (!(event.target instanceof Element)) return;
    const item = event.target.closest('.menu-list__link');
    // Native menu descendants already have their own activation handlers.
    if (!(item instanceof HTMLElement) || item.parentElement !== this.#navigationContainer) return;
    if (this.#findCustomDropdownForItem(item)) {
      this.activate(event);
      return;
    }
    this.#deactivate(this.#state.activeItem, { force: true });
  };

  #syncFormatsFallback() {
    this.#syncNavigationContainer();
    const item = this.parentElement?.querySelector('.header-nav-app-trailing:not([data-header-app-link])') ??
      this.parentElement?.querySelector('[data-header-formats-link]');
    if (!(item instanceof HTMLElement)) {
      this.#unbindFormatsFallback();
      return;
    }
    // The fallback is a sibling of the menu, so it needs the same merchant
    // typography variables that the native menu writes on its own nav element.
    const menuStyle = this.querySelector('.menu-list')?.getAttribute('style') ?? '';
    if (item.getAttribute('style') !== menuStyle) item.setAttribute('style', menuStyle);
    if (!this.#findCustomDropdownForItem(item)) {
      this.#unbindFormatsFallback();
      return;
    }
    if (item !== this.#formatsFallback) {
      this.#unbindFormatsFallback();
      this.#formatsFallback = item;
      item.addEventListener('pointerenter', this.activate);
      item.addEventListener('focusin', this.activate);
      item.addEventListener('pointerleave', this.#handleFormatsFallbackDeactivate);
      item.addEventListener('blur', this.#handleFormatsFallbackDeactivate);
      item.addEventListener('keydown', this.#handleCustomTriggerKeyDown);
    }
    const dropdown = this.#findCustomDropdownForItem(item);
    item.setAttribute('aria-haspopup', 'true');
    item.setAttribute('aria-controls', dropdown.id);
    item.setAttribute('aria-expanded', 'false');
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @param {PointerEvent} event
   */
  #handleWindowPointerMove = (event) => {
    this.#previousPointerPosition = this.#lastPointerPosition;
    this.#lastPointerPosition = {
      x: event.clientX,
      y: event.clientY,
    };

    if (!this.#activeCustomDropdown || !this.#state.activeItem) return;

    if (
      this.#isCustomDropdownRegionActive(this.#state.activeItem) ||
      this.#isPointerInCustomDropdownAimRegion(this.#state.activeItem)
    ) {
      this.#cancelCustomDropdownDeactivate();
      return;
    }

    this.#deactivate(this.#state.activeItem);
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]')
    );
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());
    this.#cancelCustomDropdownDeactivate();

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (event instanceof PointerEvent) {
      this.#previousPointerPosition = this.#lastPointerPosition;
      this.#lastPointerPosition = {
        x: event.clientX,
        y: event.clientY,
      };
    }

    if (
      event.type === 'pointerenter' &&
      this.#activeCustomDropdown &&
      this.#state.activeItem &&
      item &&
      item !== this.#state.activeItem &&
      !(
        event.relatedTarget instanceof Node &&
        this.#activeCustomDropdown.contains(event.relatedTarget)
      ) &&
      this.#isPointerMovingTowardDropdown(event) &&
      this.#isPointerInCustomDropdownAimRegion(this.#state.activeItem)
    )
      return;

    if (!item) return;

    this.#activateMenuItem(item);
  };

  /** @param {HTMLElement} item */
  #activateMenuItem(item) {
    // A top-level App link has no dropdown. Inside More, keep the native
    // overflow panel open so pointer and keyboard users can reach the link.
    if (item.hasAttribute('data-header-app-link') && !item.closest('.menu-list__list-item[slot="overflow"]')) {
      this.#deactivate(this.#state.activeItem, { force: true });
      return;
    }

    if (item == this.#state.activeItem) {
      const customDropdown = this.#findCustomDropdownForItem(item);

      if (
        customDropdown &&
        (customDropdown.hidden || this.dataset.customDropdownActive !== 'true')
      ) {
        this.#showCustomDropdown(customDropdown);
        this.headerComponent?.style.setProperty(
          '--submenu-height',
          `${customDropdown.offsetHeight}px`
        );
        this.style.setProperty('--submenu-opacity', '1');
      }

      return;
    }

    this.#cleanupMutationObserver();
    const generation = this.#activationGeneration;
    const isDefaultSlot = !item.closest('.menu-list__list-item[slot="overflow"]');

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    const customDropdown = this.#findCustomDropdownForItem(item);
    let submenu = customDropdown ?? findSubmenu(item);

    if (customDropdown) {
      this.#showCustomDropdown(customDropdown);
    } else {
      this.#hideCustomDropdown();
    }

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      const observer = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (generation !== this.#activationGeneration || this.#state.activeItem !== item || !submenu.isConnected) return;
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty(
                '--submenu-height',
                `${submenu.offsetHeight}px`
              );
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver = observer;
      observer.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        observer.disconnect();
        if (this.#submenuMutationObserver === observer) this.#submenuMutationObserver = null;
      }, 500);
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.headerComponent?.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const customDropdown = this.#findCustomDropdownForItem(this.#state.activeItem);
    const menu = customDropdown ?? findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu =
      event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node &&
      event.type === 'blur' &&
      menu?.contains(event.relatedTarget);
    const isMovingToDropdown =
      event.relatedTarget instanceof Node &&
      event.type === 'pointerleave' &&
      menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node &&
      event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu || isMovingToDropdown)
      return;

    if (event.type === 'pointerleave' && customDropdown) {
      this.#scheduleCustomDropdownDeactivate(this.#state.activeItem);
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {{ force?: boolean }} [options]
   */
  #deactivate = (item = this.#state.activeItem, options = {}) => {
    if (!item || item != this.#state.activeItem) return;
    if (!options.force && this.overflowHovered) return;
    if (!options.force && this.#isCustomDropdownRegionActive(item)) return;

    this.#cancelCustomDropdownDeactivate();

    this.#cleanupMutationObserver();
    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);
    this.#hideCustomDropdown();

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Caches the maximum height of all submenus for consistent animations
   * Stores the value in a CSS custom property for use in transitions
   */
  #cacheMaxOverflowMenuHeight() {
    const submenus = this.querySelectorAll('[ref="submenu[]"], [data-desktop-menu-dropdown]');
    const maxHeight = Math.max(
      ...Array.from(submenus)
        .filter((submenu) => submenu instanceof HTMLElement)
        .map((submenu) => submenu.offsetHeight)
    );
    this.headerComponent?.style.setProperty('--submenu-max-height', `${maxHeight}px`);
  }

  #cleanupMutationObserver() {
    this.#activationGeneration += 1;
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }

  /**
   * Normalize all menu state after resize, reconnect, or forced teardown so the
   * native overflow background cannot remain hidden without an active dropdown.
   * @param {{ resetPointer?: boolean }} [options]
   */
  #resetMenuState(options = {}) {
    this.#cancelCustomDropdownDeactivate();
    this.#cleanupMutationObserver();

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    this.dataset.customDropdownActive = 'false';
    this.ariaExpanded = 'false';

    this.querySelectorAll('[ref="menuitem"][aria-expanded]').forEach((item) => {
      if (!(item instanceof HTMLElement)) return;

      item.ariaExpanded = 'false';
    });

    this.querySelectorAll('[ref="submenu[]"][data-active]').forEach((submenu) => {
      if (!(submenu instanceof HTMLElement)) return;

      delete submenu.dataset.active;
    });

    this.querySelectorAll('[data-desktop-menu-dropdown]').forEach((dropdown) => {
      if (!(dropdown instanceof HTMLElement)) return;

      this.#cancelCustomDropdownCloseTransition(dropdown);
      delete dropdown.dataset.active;
      delete dropdown.dataset.closing;
      delete dropdown.dataset.switching;
      dropdown.hidden = true;
    });

    this.#activeCustomDropdown = null;
    this.#state.activeItem = null;

    if (options.resetPointer) {
      this.#lastPointerPosition = null;
      this.#previousPointerPosition = null;
    }
  }

  #setupCustomDropdowns() {
    this.#resetMenuState();
    this.#customDropdownByTrigger.clear();

    const dropdowns = this.querySelectorAll('[data-desktop-menu-dropdown]');
    dropdowns.forEach((dropdown) => {
      if (!(dropdown instanceof HTMLElement)) return;

      this.#cancelCustomDropdownCloseTransition(dropdown);
      dropdown.hidden = true;
      delete dropdown.dataset.active;
      delete dropdown.dataset.closing;
      delete dropdown.dataset.switching;
      dropdown.addEventListener('pointerenter', this.#handleCustomDropdownPointerEnter);
      dropdown.addEventListener('pointerleave', this.#handleCustomDropdownPointerLeave);
      dropdown.addEventListener('focusin', this.#handleCustomDropdownFocusIn);
      dropdown.addEventListener('focusout', this.#handleCustomDropdownFocusOut);
      dropdown.addEventListener('keydown', this.#handleCustomDropdownKeyDown);

      const triggers = dropdown.dataset.menuDropdownTriggers?.split(',') ?? [];
      triggers
        .map((trigger) => normalizeMenuHandle(trigger))
        .filter(Boolean)
        .forEach((trigger) => this.#customDropdownByTrigger.set(trigger, dropdown));
    });

    this.querySelectorAll('[ref="menuitem"]').forEach((item) => {
      if (!(item instanceof HTMLElement)) return;

      const dropdown = this.#findCustomDropdownForItem(item);
      if (!dropdown) return;

      item.dataset.desktopMenuCustomTrigger = 'true';
      item.setAttribute('aria-haspopup', 'true');
      item.setAttribute('aria-controls', dropdown.id);
      item.setAttribute('aria-expanded', 'false');
      item.addEventListener('pointerenter', this.#cancelCustomDropdownDeactivate);
      item.addEventListener('focusin', this.#cancelCustomDropdownDeactivate);
      item.addEventListener('keydown', this.#handleCustomTriggerKeyDown);
    });
  }

  #teardownCustomDropdowns() {
    this.#resetMenuState({ resetPointer: true });

    this.querySelectorAll('[data-desktop-menu-dropdown]').forEach((dropdown) => {
      dropdown.removeEventListener('pointerenter', this.#handleCustomDropdownPointerEnter);
      dropdown.removeEventListener('pointerleave', this.#handleCustomDropdownPointerLeave);
      dropdown.removeEventListener('focusin', this.#handleCustomDropdownFocusIn);
      dropdown.removeEventListener('focusout', this.#handleCustomDropdownFocusOut);
      dropdown.removeEventListener('keydown', this.#handleCustomDropdownKeyDown);
    });

    this.querySelectorAll('[data-desktop-menu-custom-trigger]').forEach((item) => {
      item.removeEventListener('pointerenter', this.#cancelCustomDropdownDeactivate);
      item.removeEventListener('focusin', this.#cancelCustomDropdownDeactivate);
      item.removeEventListener('keydown', this.#handleCustomTriggerKeyDown);
      delete item.dataset.desktopMenuCustomTrigger;
      item.removeAttribute('aria-haspopup');
      item.removeAttribute('aria-controls');
      item.removeAttribute('aria-expanded');
    });

    this.#customDropdownByTrigger.clear();
    this.#activeCustomDropdown = null;
  }

  /**
   * @param {HTMLElement | null | undefined} item
   * @returns {HTMLElement | null}
   */
  #findCustomDropdownForItem(item) {
    if (!(item instanceof HTMLElement)) return null;
    if (item.hasAttribute('data-header-app-link')) return null;

    const trigger = normalizeMenuHandle(item.dataset.menuLinkHandle || item.textContent || '');
    return this.#customDropdownByTrigger.get(trigger) ?? null;
  }

  /**
   * @param {HTMLElement} dropdown
   */
  #showCustomDropdown(dropdown) {
    this.#cancelCustomDropdownDeactivate();
    this.#cancelCustomDropdownCloseTransition(dropdown);
    delete dropdown.dataset.switching;

    const isSwitchingDropdown = Boolean(
      this.#activeCustomDropdown && this.#activeCustomDropdown !== dropdown
    );

    if (isSwitchingDropdown) {
      this.#hideCustomDropdown({ immediate: true });
    }

    this.#positionCustomDropdown(dropdown);
    delete dropdown.dataset.active;
    delete dropdown.dataset.closing;

    if (isSwitchingDropdown) {
      dropdown.dataset.switching = '';
    }

    dropdown.hidden = false;
    const visibleCards = Array.from(dropdown.querySelectorAll('.desktop-menu-dropdown__inner > .desktop-menu-dropdown-card'))
      .filter((card) => getComputedStyle(card).display !== 'none').length;
    dropdown.style.setProperty('--desktop-menu-dropdown-card-rows', String(Math.max(1, Math.ceil(visibleCards / 2))));

    if (!isSwitchingDropdown && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      void dropdown.offsetHeight;
    }

    dropdown.dataset.active = '';
    this.#activeCustomDropdown = dropdown;
    this.dataset.customDropdownActive = 'true';
  }

  /**
   * Anchor the dropdown to the bottom of the visible header row, not the
   * header group. In this theme the fixed dropdown is positioned in the header
   * containing block, so viewport-level header group variables create a gap.
   * @param {HTMLElement} dropdown
   */
  #positionCustomDropdown(dropdown) {
    const headerRect = this.headerComponent?.getBoundingClientRect();
    const anchorRect = (this.closest('.header__row') ?? this).getBoundingClientRect();

    if (!headerRect) return;

    dropdown.style.setProperty(
      '--desktop-menu-dropdown-top',
      `${Math.max(0, anchorRect.bottom - headerRect.top)}px`
    );
    dropdown.style.setProperty(
      '--desktop-menu-dropdown-available-height',
      `${Math.max(0, window.innerHeight - anchorRect.bottom)}px`
    );
  }

  /**
   * @param {{ immediate?: boolean }} [options]
   */
  #hideCustomDropdown(options = {}) {
    this.#cancelCustomDropdownDeactivate();

    if (!this.#activeCustomDropdown) {
      this.dataset.customDropdownActive = 'false';
      return;
    }

    const dropdown = this.#activeCustomDropdown;
    this.#cancelCustomDropdownCloseTransition(dropdown);
    delete dropdown.dataset.switching;

    delete dropdown.dataset.active;
    this.#activeCustomDropdown = null;
    this.dataset.customDropdownActive = 'false';

    if (options.immediate) {
      delete dropdown.dataset.closing;
      delete dropdown.dataset.switching;
      dropdown.hidden = true;
      return;
    }

    dropdown.dataset.closing = '';
    dropdown.hidden = false;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      delete dropdown.dataset.closing;
      delete dropdown.dataset.switching;
      dropdown.hidden = true;
      return;
    }

    const finishClose = () => {
      this.#customDropdownCloseCleanups.get(dropdown)?.();
      if (!dropdown.hasAttribute('data-closing')) return;

      delete dropdown.dataset.closing;
      delete dropdown.dataset.switching;
      dropdown.hidden = true;
    };

    const handleTransitionEnd = (event) => {
      if (event.target !== dropdown) return;
      if (event.propertyName !== 'opacity' && event.propertyName !== 'transform') return;

      finishClose();
    };

    const closeTimeout = window.setTimeout(finishClose, CUSTOM_DROPDOWN_CLOSE_TRANSITION_FALLBACK);

    const cleanup = () => {
      dropdown.removeEventListener('transitionend', handleTransitionEnd);
      window.clearTimeout(closeTimeout);
      this.#customDropdownCloseCleanups.delete(dropdown);
    };

    dropdown.addEventListener('transitionend', handleTransitionEnd);
    this.#customDropdownCloseCleanups.set(dropdown, cleanup);
  }

  /**
   * @param {HTMLElement} dropdown
   */
  #cancelCustomDropdownCloseTransition(dropdown) {
    this.#customDropdownCloseCleanups.get(dropdown)?.();

    delete dropdown.dataset.closing;
  }

  #handleOverflowMenuPointerLeave = () => {
    if (this.#activeCustomDropdown) {
      this.#scheduleCustomDropdownDeactivate(this.#state.activeItem);
      return;
    }

    this.#deactivate();
  };

  /**
   * Move keyboard focus from a custom top-level trigger into its dropdown.
   * @param {KeyboardEvent} event
   */
  #handleCustomTriggerKeyDown = (event) => {
    if (!(event.currentTarget instanceof HTMLElement)) return;

    const item = event.currentTarget;

    if (event.key === 'Escape') {
      this.#deactivate(item, { force: true });
      return;
    }

    if (event.key !== 'Tab' || event.shiftKey) return;

    const dropdown = this.#findCustomDropdownForItem(item);
    if (!dropdown || dropdown.hidden) return;

    const firstFocusable = getFocusableElements(dropdown)[0];
    if (!firstFocusable) return;

    event.preventDefault();
    firstFocusable.focus();
  };

  #handleCustomDropdownPointerEnter = () => {
    this.#cancelCustomDropdownDeactivate();
  };

  #handleCustomDropdownFocusIn = () => {
    this.#cancelCustomDropdownDeactivate();
  };

  /**
   * @param {PointerEvent} event
   */
  #handleCustomDropdownPointerLeave = (event) => {
    if (
      event.relatedTarget instanceof Node &&
      this.#state.activeItem?.contains(event.relatedTarget)
    )
      return;

    if (this.#activeCustomDropdown?.contains(document.activeElement)) return;

    this.#scheduleCustomDropdownDeactivate(this.#state.activeItem);
  };

  /**
   * @param {FocusEvent} event
   */
  #handleCustomDropdownFocusOut = (event) => {
    if (
      event.relatedTarget instanceof Node &&
      (this.#activeCustomDropdown?.contains(event.relatedTarget) ||
        this.#state.activeItem?.contains(event.relatedTarget))
    )
      return;

    this.#deactivate(this.#state.activeItem, { force: true });
  };

  /**
   * Keep reverse tab and Escape behavior coherent once focus is inside a custom dropdown.
   * @param {KeyboardEvent} event
   */
  #handleCustomDropdownKeyDown = (event) => {
    if (!(event.currentTarget instanceof HTMLElement)) return;

    const dropdown = event.currentTarget;
    const activeItem = this.#state.activeItem;

    if (event.key === 'Escape') {
      event.preventDefault();
      activeItem?.focus();
      this.#deactivate(activeItem, { force: true });
      return;
    }

    if (event.key !== 'Tab' || !activeItem) return;

    if (!event.shiftKey) {
      // Detached triggers follow their dropdown in DOM order. Native Tab would
      // return to the trigger and then re-enter the dropdown indefinitely.
      if (this.contains(activeItem)) return;
      const items = getFocusableElements(dropdown);
      if (document.activeElement !== items.at(-1)) return;

      const destinations = getFocusableElements(document.body).filter((item) =>
        item.tabIndex >= 0 && !item.closest('[inert]') && getComputedStyle(item).visibility === 'visible'
      );
      const triggerIndex = destinations.indexOf(activeItem);
      const next = triggerIndex < 0 ? null : destinations[triggerIndex + 1];
      if (next) {
        this.#deactivate(activeItem, { force: true });
        event.preventDefault();
        next.focus();
      } else {
        // Let the browser advance out of the document from the trailing trigger.
        activeItem.focus();
        this.#deactivate(activeItem, { force: true });
      }
      return;
    }

    const firstFocusable = getFocusableElements(dropdown)[0];
    if (firstFocusable && document.activeElement !== firstFocusable) return;

    event.preventDefault();
    activeItem.focus();
  };

  /**
   * Detached custom dropdowns need a small pointer bridge between the trigger
   * and the fixed panel because they do not share one hoverable parent.
   * @param {HTMLElement | null} item
   */
  #scheduleCustomDropdownDeactivate(item, allowBridge = true) {
    if (!item) return;

    this.#cancelCustomDropdownDeactivate();
    this.#customDropdownCloseTimeout = window.setTimeout(() => {
      this.#customDropdownCloseTimeout = null;

      if (this.#isCustomDropdownRegionActive(item)) return;

      if (allowBridge && this.#isPointerInCustomDropdownAimRegion(item)) {
        this.#scheduleCustomDropdownDeactivate(item, false);
        return;
      }

      this.#deactivate(item);
    }, CUSTOM_DROPDOWN_CLOSE_DELAY);
  }

  #cancelCustomDropdownDeactivate = () => {
    if (!this.#customDropdownCloseTimeout) return;

    window.clearTimeout(this.#customDropdownCloseTimeout);
    this.#customDropdownCloseTimeout = null;
  };

  /**
   * @param {HTMLElement | null} item
   * @returns {boolean}
   */
  #isCustomDropdownRegionActive(item) {
    const dropdown = this.#findCustomDropdownForItem(item);

    return Boolean(
      item?.matches(':hover, :focus-within') ||
      dropdown?.matches(':hover, :focus-within') ||
      this.#isPointerInNavigationRow() ||
      this.#isPointerInCustomDropdownRegion(item) ||
      dropdown?.contains(document.activeElement)
    );
  }

  // Native and detached links form one navigation row regardless of order.
  // Use their visible bounds, excluding logo/account controls in other slots.
  #isPointerInNavigationRow() {
    const pointer = this.#lastPointerPosition;
    if (!pointer || this.headerComponent?.dataset.menuStyle !== 'menu') return false;
    const links = [...(this.parentElement?.querySelectorAll(':scope > .menu-list__link') ?? [])];
    const rects = [this, ...links]
      .filter(item => item.getClientRects().length && getComputedStyle(item).visibility === 'visible')
      .map(item => item.getBoundingClientRect());
    if (!rects.length) return false;
    return pointer.x >= Math.min(...rects.map(rect => rect.left)) &&
      pointer.x <= Math.max(...rects.map(rect => rect.right)) &&
      pointer.y >= Math.max(...rects.map(rect => rect.top)) &&
      pointer.y <= Math.min(...rects.map(rect => rect.bottom));
  }

  /**
   * The fixed dropdown is detached from the top-level menu item, so CSS hover on
   * the menu item is not enough. Treat the trigger and dropdown rectangles as
   * the active pointer region.
   *
   * @param {HTMLElement | null} item
   * @returns {boolean}
   */
  #isPointerInCustomDropdownRegion(item) {
    const pointer = this.#lastPointerPosition;
    const dropdown = this.#findCustomDropdownForItem(item);

    if (!pointer || !item || !dropdown || dropdown.hidden) return false;

    const itemRect = item.getBoundingClientRect();
    const dropdownRect = dropdown.getBoundingClientRect();

    if (dropdownRect.width <= 0 || dropdownRect.height <= 0) return false;

    const isInsideTrigger =
      pointer.x >= itemRect.left &&
      pointer.x <= itemRect.right &&
      pointer.y >= itemRect.top &&
      pointer.y <= itemRect.bottom;
    const isInsideDropdown =
      pointer.x >= dropdownRect.left &&
      pointer.x <= dropdownRect.right &&
      pointer.y >= dropdownRect.top &&
      pointer.y <= dropdownRect.bottom;

    return isInsideTrigger || isInsideDropdown;
  }

  /**
   * A narrow "menu aim" region prevents diagonal movement into a fixed dropdown
   * from accidentally activating a neighboring top-level trigger. This is not a
   * persistent hover state; pointer movement outside this path closes the menu.
   *
   * @param {HTMLElement | null} item
   * @returns {boolean}
   */
  #isPointerInCustomDropdownAimRegion(item) {
    const pointer = this.#lastPointerPosition;
    const dropdown = this.#findCustomDropdownForItem(item);

    if (!pointer || !item || !dropdown || dropdown.hidden) return false;

    const itemRect = item.getBoundingClientRect();
    const dropdownRect = dropdown.getBoundingClientRect();

    if (dropdownRect.width <= 0 || dropdownRect.height <= 0) return false;

    const aimTop = itemRect.top + itemRect.height * 0.72;
    const aimBottom = dropdownRect.top + 10;

    return (
      pointer.x >= dropdownRect.left &&
      pointer.x <= dropdownRect.right &&
      pointer.y >= aimTop &&
      pointer.y <= aimBottom
    );
  }

  /**
   * @param {PointerEvent | null} [event]
   * @returns {boolean}
   */
  #isPointerMovingTowardDropdown(event = null) {
    if (
      event instanceof PointerEvent &&
      Number.isFinite(event.movementY) &&
      event.movementY !== 0
    ) {
      return event.movementY > 0;
    }

    if (!this.#previousPointerPosition || !this.#lastPointerPosition) return false;

    return this.#lastPointerPosition.y > this.#previousPointerPosition.y;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const moreItem = element.matches('[slot="more"]') ? element : element.closest('[slot="more"]');

  if (moreItem) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(moreItem.parentElement?.querySelector('[slot="overflow"]'));
  }

  const menuItem = element.closest('.menu-list__list-item') ?? element;
  const item = element.closest('[ref="menuitem"]') ?? menuItem.querySelector('[ref="menuitem"]');

  return item instanceof HTMLElement ? item : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * Normalizes a menu label to the same shape as Liquid's `handleize` filter.
 * @param {string} value
 * @returns {string}
 */
function normalizeMenuHandle(value) {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * @param {HTMLElement} element
 * @returns {HTMLElement[]}
 */
function getFocusableElements(element) {
  return Array.from(element.querySelectorAll(FOCUSABLE_SELECTOR))
    .filter((focusable) => focusable instanceof HTMLElement)
    .filter((focusable) => focusable.getClientRects().length > 0);
}
